import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import { FaPlus, FaTrophy } from "react-icons/fa";
import TournamentList from "../../TournamentList/TournamentList";
import { SkeletonBoard } from "../../UI/DesignStandards";

const OrganizationTournaments = () => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [tournaments, setTournaments] = useState([]);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentOrganization) {
      fetchTournaments();
    }
  }, [currentOrganization]);

  const fetchTournaments = async () => {
    if (!currentOrganization) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("tournaments")
        .select(
          `
          *,
          tournamentEvents (
            eventName,
            bowlerQuantity,
            isPrimary
          ),
          organizations (
            id,
            name
          ),
          squads (
            id,
            isDeleted,
            squadDate,
            bowlingCenters (
              id,
              centerName
            )
          )
        `
        )
        .eq("organizationID", currentOrganization.id)
        .is("isDeleted", false)
        .order("startDate", { ascending: false });

      if (error) throw error;

      setTournaments(data || []);
      setFilteredTournaments(data || []);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
      notify.error("Failed to load tournaments");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6" role="status" aria-label="Loading tournaments">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Tournaments
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Tournaments for {currentOrganization?.name}.
            </p>
          </div>
          <Link
            to="/create-tournament"
            className="inline-flex items-center self-start min-h-11 bg-brand-gold text-brand-charcoal px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide hover:bg-brand-gold/90"
            aria-label="Create new tournament"
          >
            <FaPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create Tournament
          </Link>
        </div>
      </header>

      {/* Reuse TournamentList component with organization-filtered data */}
      <TournamentList
        tournaments={tournaments}
        filteredTournaments={filteredTournaments}
        setFilteredTournaments={setFilteredTournaments}
        showOrganizationFilter={false}
        onTournamentUpdate={fetchTournaments}
      />
    </div>
  );
};

export default OrganizationTournaments;
